import { useMemo, useRef, useEffect, useState, useCallback } from 'react';
interface UseFormDirtyStateOptions<T> {
    values: T;
    onDirtyChange?: (isDirty: boolean) => void;
}

/**
 * A hook for managing form dirty state by comparing current values with original values.
 *
 * @returns Object containing:
 * - isDirty: boolean indicating if the form has unsaved changes
 * - updateOriginalValues: function to update original values to current values (useful after save) or initialize original values
 * - originalValues: the current original values (read-only)
 *
 * @example
 * ```tsx
 * const formValues = {
 *   name: 'Horn',
 *   tagline: 'Horn is a beautiful village',
 *   description: 'Village in the Dutch province of Limburg',
 * };
 *
 * const { isDirty, updateOriginalValues } = useFormDirtyState({
 *   values: formValues,
 *   onDirtyChange: (dirty) => console.log('Form is dirty:', dirty)
 * });
 *
 * // After successful save
 * const handleSave = async () => {
 *   await saveData(formValues);
 *   updateOriginalValues(); // Reset dirty state
 * };
 * ```
 */
export function useFormDirtyState<T>({ values, onDirtyChange }: UseFormDirtyStateOptions<T>) {
    const [originalValues, setOriginalValues] = useState<T | null>(null);
    const isInitializedRef = useRef(false);

    // Initialize original values
    useEffect(() => {
        if (!isInitializedRef.current && values) {
            setOriginalValues(JSON.parse(JSON.stringify(values)));
            isInitializedRef.current = true;
        }
    }, [values]);

    // Calculate if form is dirty
    const isDirty = useMemo(() => {
        if (!originalValues || !values) return false;

        return Object.keys(values).some((field) => {
            const currentValue = values[field as keyof T];
            const originalValue = originalValues[field as keyof T];

            // Handle nested objects and arrays
            return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
        });
    }, [values, originalValues]);

    // Notify parent of dirty state changes
    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    // Reset original values (useful after successful save)
    const updateOriginalValues = useCallback(
        (newOriginalValues?: Partial<T>) => {
            if (newOriginalValues && originalValues) {
                setOriginalValues({
                    ...originalValues,
                    ...newOriginalValues,
                });
            } else {
                setOriginalValues(JSON.parse(JSON.stringify(values)));
            }
        },
        [originalValues, values]
    );

    return {
        isDirty,
        updateOriginalValues,
        originalValues,
    };
}
